import { createElement, useCallback, useEffect } from 'react';
import { RootState } from '../redux/AppReducer';
import { FilterActions } from '../redux/FilterActions';
import { ApolloActions } from '../redux/ApolloActions';
import { GET_CURRENT_EVENTS } from '../graphql/queries/events';
import { GET_TAXONOMY_NAVBAR } from '../graphql/queries/taxonomyNavbar';
import { GET_FILTERS } from '../graphql/queries/filters';
import { TokenError, redirectUrl } from '../common/constants';
import { IirisIdentityKey, getLocalStorageValue } from '../helpers/authenticationHelper';
import { GET_BUSINESS_NAVBAR } from '../graphql/queries/businessNavBar';
import { UserActions } from '../redux/UserActions';
import { ITrackingEvent, TrackingEventStatus, TrackingEventType } from '../common/view-model';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';
import { AppActions } from '../redux/AppActions';
import { useAppDispatch } from '../redux/store';
import CompanyDetailsComponent from '../components/authentication/company-details/CompanyDetailsComponent';
import { manualRefreshToken } from '../helpers/manualRefreshToken';
import { AuthActions } from '../redux/AuthActions';

const useHasData = () => {
	const dispatch = useAppDispatch();

	const getAnonymousUserToken = () => dispatch({ type: UserActions.GET_ANONYMOUS_USER_TOKEN });

	const storeState = useSelector(({ appState: state }: RootState) => state.storeState);

	const isRefreshingToken = useSelector(
		({ appState: state }: RootState) => state.authState?.isRefreshingToken
	);

	const tokenHasError = useSelector(
		({ appState: state }: RootState) => state.authState?.tokenHasError
	);

	const hasAllData = useSelector(
		({ appState: state }: RootState) =>
			state.storeState?.taxonomyNavbar &&
			state.storeState?.taxonomyNavbar?.length !== 0 &&
			state.storeState.businessServicesNavbar?.length !== 0 &&
			state.storeState.filters !== undefined
	);

	const beaconProfileVerify = useSelector((state: RootState) => state.appState.userBeaconInfo);
	const beaconTokenUnathorized = useSelector(
		(state: RootState) => state.appState.authState?.tokenHasError
	);

	const isAuthenticated = useSelector(
		(state: RootState) => state.appState.authState?.isAuthenticated
	);

	const isPublicAuthorized = useSelector(
		(state: RootState) => state.appState.authState?.isPublicAuthorized
	);

	const logout = useCallback(() => dispatch(AuthActions.logout()), [dispatch]);

	const userState = useSelector(({ appState: state }: RootState) => state.userState);

	const { loadProfileProgress } = useSelector((state: RootState) => state.appState);

	const getEvent = () => dispatch(ApolloActions.query({ query: GET_CURRENT_EVENTS }));

	const getTaxonomyNavbar = () => dispatch(ApolloActions.query({ query: GET_TAXONOMY_NAVBAR }));

	const getFilters = (variables: any = null) =>
		dispatch(ApolloActions.query({ query: GET_FILTERS, variables }));

	const getBusinessServicesNavBar = () =>
		dispatch(ApolloActions.query({ query: GET_BUSINESS_NAVBAR }));

	const createBeaconProfileCallback = useCallback(() => {
		const trackingPayload: ITrackingEvent = {
			transactionId: uuidv4(),
			entityId: getLocalStorageValue(IirisIdentityKey.USER_ID) || '',
			status: TrackingEventStatus.SUCCESS,
			type: TrackingEventType.PUBLIC_USER_CONVERTED,
			timeStamp: new Date().toISOString(),
			eventPayload: {
				id: getLocalStorageValue(IirisIdentityKey.USER_ID) || '',
				name: getLocalStorageValue(IirisIdentityKey.USER_ID) || '',
				type: TrackingEventType.PUBLIC_USER_CONVERTED,
				url: window.location.href,
				tags: [],
				metadata: {
					user_id: getLocalStorageValue(IirisIdentityKey.USER_ID) || '',
					enviroment: import.meta.env.VITE_ENV_NAME
				}
			}
		};
		return dispatch(
			UserActions.createUserProfileBeacon(
				{
					content: createElement(CompanyDetailsComponent, null),
					showCloseIcon: true,
					canScroll: true,
					isCompanyDetailsModal: true,
					closeOnDocumentClick: false
				},
				trackingPayload
			)
		);
	}, []);

	useEffect(() => {
		if (!isAuthenticated && !isPublicAuthorized) {
			getAnonymousUserToken();
		}
	}, [isAuthenticated]);

	const finishProgressCallback = useCallback(() => {
		return dispatch(AppActions.finishProgress());
	}, []);

	useEffect(() => {
		if (!isAuthenticated && !isPublicAuthorized) {
			return;
		}

		if (!storeState?.taxonomyNavbar && beaconTokenUnathorized === 0) {
			getTaxonomyNavbar();
		}
		if (!storeState?.businessServicesNavbar && beaconTokenUnathorized === 0) {
			getBusinessServicesNavBar();
		}
	}, [
		storeState?.taxonomyNavbar,
		storeState?.businessServicesNavbar,
		userState?.hasLoadProfile,
		userState?.isAnonymous,
		isRefreshingToken
	]);

	useEffect(() => {
		if (!isAuthenticated && !isPublicAuthorized) {
			return;
		}
		if (storeState?.getCurrentEvent === undefined && beaconTokenUnathorized === 0) {
			getEvent();
		}
	}, [
		storeState?.getCurrentEvent,
		userState?.hasLoadProfile,
		userState?.isAnonymous,
		isRefreshingToken
	]);

	useEffect(() => {
		if (hasAllData) {
			dispatch(FilterActions.mapQueryToFilters());
		}
	}, [hasAllData]);

	useEffect(() => {
		if (isRefreshingToken && isAuthenticated) {
			return;
		}
		if (!isAuthenticated && !isPublicAuthorized) {
			return;
		}
		if (storeState?.filters) {
			return;
		}
		if (userState?.hasLoadProfile && beaconTokenUnathorized === 0) {
			getFilters({ templateId: userState?.ingredientsTemplateId?.toString() || '1' });
		} else {
			if (!isAuthenticated && userState?.isAnonymous && beaconTokenUnathorized === 0) {
				getFilters({ templateId: '1' });
			}
		}
	}, [
		userState?.hasLoadProfile,
		userState?.ingredientsTemplateId,
		userState?.isAnonymous,
		isRefreshingToken,
		storeState?.filters
	]);

	useEffect(() => {
		if (beaconProfileVerify?.origin !== undefined) {
			if (beaconProfileVerify?.status === true) {
				dispatch(
					UserActions.updaUserBeaconInfoStatus({
						origin: undefined,
						status: undefined
					})
				);
				let redirect_url = localStorage.getItem(redirectUrl);
				if (redirect_url !== null) {
					localStorage.removeItem(redirectUrl);
					window.location.href = redirect_url;
				} else {
					window.location.href = '/landing';
				}
			} else {
				createBeaconProfileCallback();
			}
		}
	}, [beaconProfileVerify]);

	useEffect(() => {
		if (isRefreshingToken === true) {
			return;
		}
		if (
			isAuthenticated &&
			!userState?.user &&
			isRefreshingToken !== undefined &&
			beaconTokenUnathorized === 0
		) {
			dispatch({ type: UserActions.GET_USER_PROFILE, payload: undefined });
		}
	}, [isAuthenticated, isRefreshingToken]);

	useEffect(() => {
		if (tokenHasError !== undefined && tokenHasError === 1) {
			dispatch(AppActions.setGeneralError(TokenError));
			manualRefreshToken()
				.then(() => {
					setTimeout(() => {
						window.location.href = window.location.href;
					}, 4000);
				})
				.catch(() => {
					document?.getElementById('irisLogoutBtn')?.click();
					logout();
					setTimeout(() => {
						window.location.href = window.location.href;
					}, 4000);
				});
		}
	}, [tokenHasError]);

	useEffect(() => {
		if (userState?.hasLoadProfile && loadProfileProgress === 90) {
			finishProgressCallback();
		}
	}, [userState?.hasLoadProfile, finishProgressCallback, loadProfileProgress]);
};

export default useHasData;
